import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Flame, Sparkles, Lock, CheckCircle2, ClipboardCheck, ArrowRight } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { Card } from '../ui/Card';
import { AchievementsSection } from './AchievementsSection';
import { DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WorldZone } from '../shared/WorldZone';
import { Button } from '../ui/Button';
import type { AssessmentReport } from '../../assessment';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  // Calculate overall stats
  const totalQuestions = MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0);
  // Calculate overall progress
  const {
    masteredCount,
    inProgressCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    let mastered = 0;
    let inProgress = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 95) mastered++;else if (answered > 0 || mastery > 0) inProgress++;
    });
    return {
      masteredCount: mastered,
      inProgressCount: inProgress,
      totalQuestionsAnswered: totalQs
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  // Calculate streak (simplified for this view)
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  // Determine unlocked special Djays based on achievements logic
  const unlockedVariants = useMemo(() => {
    const variants: {
      variant: DjayVariant;
      name: string;
      description: string;
      unlocked: boolean;
    }[] = [{
      variant: 'pixel',
      name: 'DJ Pixel',
      description: 'Honderdtal bereikt',
      unlocked: totalQuestionsAnswered >= 100
    }, {
      variant: 'blaze',
      name: 'DJ Blaze',
      description: '3 volle weken achter elkaar',
      unlocked: consecutiveFullWeeks >= 3
    }, {
      variant: 'frost',
      name: 'DJ Frost',
      description: '3 vakken afgerond',
      unlocked: masteredCount >= 3
    }, {
      variant: 'royal',
      name: 'DJ Royal',
      description: 'Alle vakken afgerond',
      unlocked: masteredCount >= MODULES.length
    }, {
      variant: 'cosmic',
      name: 'DJ Cosmic',
      description: 'Level 15 bereikt',
      unlocked: state.stats.level >= 15
    }, {
      variant: 'shadow',
      name: 'DJ Shadow',
      description: '30.000 XP behaald',
      unlocked: state.stats.xp >= 30000
    }];
    return variants;
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state.stats.level, state.stats.xp]);
  return <PageContainer data-id="element-1493">
      {/* Hero Header with Djay — using shared PageHero */}
      <motion.div variants={itemVariants} data-id="element-1494">
        <PageHero title="Mijn Trofeeën" subtitle="Verzamel achievements door te oefenen! Hoe moeilijker, hoe zeldzamer de trofee. 🏆" gradientClasses="from-amber-500 via-orange-500 to-rose-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={Trophy} iconBgClass="bg-amber-400" floatingEmojis={['🏆', '⭐']} data-id="element-1495">
          <HeroStatPill icon={Star} data-id="element-1496">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1497">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1498">
            {currentStreak} dagen deze week
          </HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Main Content Grid */}
      <motion.div variants={itemVariants} data-id="element-1499">
        <WorldZone icon={Sparkles} title="Mascotte Collectie" accentColor="bg-orange-100 text-orange-600" djayMessage="Verzamel speciale Djays en kies je favoriet!" djayEmotion="excited" djayVariant={selectedVariant} data-id="element-1500">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-id="element-1501">
            {unlockedVariants.map((item, index) => {
            const isSelected = selectedVariant === item.variant;
            return <motion.div key={item.variant} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} data-id="element-1502">
                  <Card className={`p-4 flex flex-col items-center text-center h-full border-2 transition-all relative overflow-hidden rounded-[2rem]
                    ${item.unlocked ? isSelected ? 'bg-orange-50 border-orange-400 shadow-md ring-2 ring-orange-400 ring-offset-2' : 'bg-white border-orange-200 shadow-sm hover:shadow-md hover:-translate-y-1 cursor-pointer' : 'bg-slate-50 border-slate-200 opacity-60'}`} onClick={() => item.unlocked && !isSelected && setSelectedVariant(item.variant)} data-id="element-1503">
                    {isSelected && <div className="absolute top-0 inset-x-0 h-1 bg-orange-400" data-id="element-1504" />}

                    <div className="mb-3 relative" data-id="element-1505">
                      {!item.unlocked && <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px] rounded-full" data-id="element-1506">
                          <div className="w-8 h-8 bg-slate-800/60 rounded-full flex items-center justify-center" data-id="element-1507">
                            <Lock className="w-4 h-4 text-white" data-id="element-1508" />
                          </div>
                        </div>}
                      <div className={!item.unlocked ? 'grayscale' : ''} data-id="element-1509">
                        <DjayAvatar size="lg" emotion={item.unlocked ? isSelected ? 'excited' : 'celebrating' : 'sleeping'} variant={item.variant} animate={item.unlocked} data-id="element-1510" />
                      </div>
                    </div>

                    <h3 className={`font-bold text-sm mb-1 ${item.unlocked ? isSelected ? 'text-orange-700' : 'text-slate-800' : 'text-slate-500'}`} data-id="element-1511">
                      {item.name}
                    </h3>
                    <p className="text-[10px] text-slate-400 font-medium leading-tight mb-3 flex-1" data-id="element-1512">
                      {item.description}
                    </p>

                    {item.unlocked && <div className="mt-auto w-full" data-id="element-1513">
                        {isSelected ? <div className="bg-orange-100 text-orange-700 text-[10px] font-bold py-1 px-2 rounded-full flex items-center justify-center gap-1" data-id="element-1514">
                            <CheckCircle2 className="w-3 h-3" data-id="element-1515" /> Actief
                          </div> : <Button variant="secondary" size="sm" className="w-full bg-slate-100 hover:bg-orange-100 text-slate-600 hover:text-orange-700 text-[10px] font-bold py-1 px-2 rounded-full transition-colors h-auto" onClick={e => {
                    e.stopPropagation();
                    setSelectedVariant(item.variant);
                  }} data-id="element-1516">
                            Selecteer
                          </Button>}
                      </div>}
                  </Card>
                </motion.div>;
          })}
          </div>
        </WorldZone>
      </motion.div>

      {/* Achievements Section with Djay Rarity System */}
      <motion.div variants={itemVariants} data-id="element-1517">
        <AchievementsSection state={state} totalQuestionsAnswered={totalQuestionsAnswered} masteredCount={masteredCount} currentStreak={currentStreak} data-id="element-1518" />
      </motion.div>

      {/* Encouragement Footer */}
      <motion.div variants={itemVariants} data-id="element-1519">
        <WorldZone icon={Star} title="Goed bezig!" accentColor="bg-purple-100 text-purple-600" isLast={true} data-id="element-1520">
          <Card className="p-6 bg-gradient-to-r from-purple-100 via-fuchsia-100 to-pink-100 border-2 border-purple-200 text-center rounded-[2rem] shadow-lg relative overflow-hidden" data-id="element-1521">
            <motion.div className="absolute top-2 left-8 text-2xl" animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1522">
              ✨
            </motion.div>
            <motion.div className="absolute bottom-2 right-8 text-2xl" animate={{
            rotate: [360, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1523">
              ⭐
            </motion.div>

            <div className="relative z-10" data-id="element-1524">
              <div className="flex items-center justify-center gap-3 mb-3" data-id="element-1525">
                <DjayAvatar size="lg" emotion="encouraging" variant={selectedVariant} animate data-id="element-1526" />
              </div>
              <p className="text-purple-700 font-medium text-lg max-w-md mx-auto" data-id="element-1527">
                Fouten maken mag, daar leer je juist van. Blijf proberen en
                verzamel meer trofeeën! 💪
              </p>
            </div>
          </Card>
        </WorldZone>
      </motion.div>
    </PageContainer>;
}